/**
 * Visitors (address book) API — pagedVisitorsGrid reads + every visitor
 * mutation. Customer-scoped by design (no location argument). Demo mode
 * short-circuits to the in-memory store.
 */

import { Injectable, inject } from '@angular/core';

import { RDX_VISITORS_ENV } from '../core/env';
import { GqlClient } from '../core/gql-client';
import {
  applySort,
  demoCredentials,
  demoDelay,
  demoTagLibrary,
  demoId,
  demoVisitors,
  demoVisits,
  groupMatches,
  page,
  searchMatches,
} from '../demo/demo-store';
import { DEMO_VISITOR_TYPES } from '../demo/demo-data';
import { DEMO_HISTORY, demoAt } from '../demo/demo-records';
import type { RecordHistory } from '../address-book/record-panel/record-summary';
import type { ApiCredential, ApiVisitor, FilterGroup, SortSpec } from './api-types';
import { adaptVisitor, toVisitorInput } from './platform-mapping';

export interface VisitorsGridVars {
  skip: number;
  take: number;
  searchTerm?: string | null;
  filter?: FilterGroup;
  sort?: SortSpec[];
}

export interface VisitorsPage {
  visitors: ApiVisitor[];
  total: number;
}

export interface VisitorInput {
  firstName: string;
  lastName: string;
  email?: string;
  mobile?: string;
  countryCode?: string;
  company?: string;
  jobTitle?: string;
  carRegistration?: string;
  accessNeeds?: string;
  /** Demo only: `updateVisitor` doesn't accept it (`NEEDS_API.presets`). */
  dietaryRequirements?: string;
  /** Demo only: `updateVisitor` doesn't accept them (`NEEDS_API.emergency`). */
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  visitorTypeId?: string;
  vip?: boolean;
  isPrivate?: boolean;
  isInAddressBook?: boolean;
  tags?: string[];
}

/**
 * A person, as the platform holds one: no type (the type is the visit's),
 * accessibility needs the visitor gives themselves, and no `createdAt` — the
 * address book's "Created" column reads the id's timestamp (`adaptVisitor`).
 */
const FIELDS = `
  _id firstName lastName email mobile countryCode company jobTitle carRegistration
  accessibilityNeeds dietaryRequirements emergencyContactName emergencyContactPhone
  picture tags vip isPrivate isInAddressBook pin`;

/**
 * A person's documents (`credentialsFor`, `visitor:view`). Visit-scoped ones
 * come back too (`visitId`); the record panel leaves them out. No file: the
 * API records the fact, not the artefact (`NEEDS_API.uploads`).
 */
const CREDENTIALS_QUERY = `query CredentialsFor($personId: ID!) {
  credentialsFor(personId: $personId) {
    _id label kind method assurance issuedAt expiresAt reqId visitId
    verifiedBy { name }
  }
}`;

/** Withdraw one (`visitor:credential:verify`). Soft: the API keeps it for the audit. */
const REVOKE_CREDENTIAL_MUTATION = `mutation RevokeCredential($id: ID!, $note: String) {
  revokeCredential(id: $id, note: $note)
}`;

/** How many people the CSV import creates at once. */
const IMPORT_CONCURRENCY = 4;

const PAGED_QUERY = `query PagedVisitorsGrid(
  $skip: Int, $take: Int, $searchTerm: String, $filter: FilterGroupInput, $sort: [SortInput!]
) {
  pagedVisitorsGrid(skip: $skip, take: $take, searchTerm: $searchTerm, filter: $filter, sort: $sort) {
    total
    visitors { ${FIELDS} }
  }
}`;

const TAKE_CAP = 100;
const EXPORT_MAX_ROWS = 10_000;

const DEMO_SEARCH_PATHS = [
  'firstName',
  'lastName',
  'email',
  'company',
  'jobTitle',
  'mobile',
  'carRegistration',
  'tags',
  'visitorType',
];

@Injectable({ providedIn: 'root' })
export class VisitorsApiService {
  private readonly gql = inject(GqlClient);

  /**
   * Access needs, dietary requirements and the emergency contact save in the
   * demo only: `CreateVisitorInput` has none of them (`NEEDS_API.presets`,
   * `NEEDS_API.emergency`), so the platform reads them and can't write them.
   */
  readonly careWritable = !RDX_VISITORS_ENV.PLATFORM_MODE;

  async getPage(vars: VisitorsGridVars): Promise<VisitorsPage> {
    if (!RDX_VISITORS_ENV.PLATFORM_MODE) {
      await demoDelay();
      // The demo's book holds its saved contacts only, as the `addressBook`
      // query does; the generated visits' people are one-off visitors.
      // (`pagedVisitorsGrid` on the platform doesn't filter on it.)
      let rows = demoVisitors.filter(
        (v) => v.isInAddressBook !== false && groupMatches(v, vars.filter),
      );
      if (vars.searchTerm)
        rows = rows.filter((v) => searchMatches(v, vars.searchTerm!, DEMO_SEARCH_PATHS));
      rows = applySort(rows, vars.sort?.length ? vars.sort : [{ field: 'lastName', order: 'ASC' }]);
      const { rows: pageRows, total } = page(rows, vars.skip, vars.take);
      return { visitors: pageRows, total };
    }
    const data = await this.gql.gql<{ pagedVisitorsGrid: VisitorsPage }>(PAGED_QUERY, {
      ...vars,
      take: Math.min(vars.take, TAKE_CAP),
    });
    return {
      ...data.pagedVisitorsGrid,
      visitors: data.pagedVisitorsGrid.visitors.map(adaptVisitor),
    };
  }

  async fetchAll(
    vars: Omit<VisitorsGridVars, 'skip' | 'take'>,
  ): Promise<{ visitors: ApiVisitor[]; truncated: boolean }> {
    const all: ApiVisitor[] = [];
    let skip = 0;
    for (;;) {
      const { visitors, total } = await this.getPage({ ...vars, skip, take: TAKE_CAP });
      all.push(...visitors);
      skip += TAKE_CAP;
      if (all.length >= total || !visitors.length) return { visitors: all, truncated: false };
      if (all.length >= EXPORT_MAX_ROWS) return { visitors: all, truncated: true };
    }
  }

  /** One person by id, or null (a stale or foreign id). */
  async getById(id: string): Promise<ApiVisitor | null> {
    const { visitors } = await this.getPage({
      skip: 0,
      take: 1,
      filter: { op: 'AND', rules: [{ field: '_id', operator: 'EQ', value: id }] },
    });
    return visitors[0] ?? null;
  }

  /** Everything one person holds: their documents. */
  async credentialsFor(personId: string): Promise<ApiCredential[]> {
    if (!RDX_VISITORS_ENV.PLATFORM_MODE) {
      await demoDelay();
      return (demoCredentials[personId] ?? []).map((c) => ({ ...c }));
    }
    const data = await this.gql.gql<{ credentialsFor: ApiCredential[] }>(CREDENTIALS_QUERY, {
      personId,
    });
    return data.credentialsFor;
  }

  /** Delete a document: the API withdraws it (soft, kept for the audit). */
  async revokeCredential(id: string): Promise<void> {
    if (!RDX_VISITORS_ENV.PLATFORM_MODE) {
      for (const list of Object.values(demoCredentials)) {
        const i = list.findIndex((c) => c._id === id);
        if (i >= 0) list.splice(i, 1);
      }
      return demoDelay();
    }
    await this.gql.gql(REVOKE_CREDENTIAL_MUTATION, { id });
  }

  /**
   * The record's visit history, for the summary strip. Needs API
   * (`NEEDS_API.visitHistory`): null on the platform, so the strip is left
   * out. The demo reads the cast's count and last visit, and its next booked
   * day and sign-in (`demo-records.ts`).
   */
  async historyFor(v: ApiVisitor): Promise<RecordHistory | null> {
    if (RDX_VISITORS_ENV.PLATFORM_MODE) return null;
    const extra = DEMO_HISTORY[v._id] ?? {};
    const count = v.visitCount ?? 0;
    // The first visit: the demo's own, else the day after they joined the book.
    let first: string | null = null;
    if (count > 0 && (extra.first !== undefined || v.createdAt)) {
      const d = extra.first !== undefined ? new Date() : new Date(v.createdAt!);
      d.setDate(d.getDate() + (extra.first !== undefined ? -extra.first : 1));
      first = d.toISOString();
    }
    return {
      visitCount: count,
      firstVisitOn: first,
      lastVisitOn: v.lastVisitOn ?? null,
      hereSince: extra.hereSince ? demoAt(0, extra.hereSince) : null,
      nextVisitAt: extra.next ? demoAt(extra.next[0], extra.next[1]) : null,
      addedAt: v.createdAt ?? null,
    };
  }

  /**
   * The organisation's tags, for the record panel's tag editor. Demo: every
   * location's library and the contacts' tags. Platform: tags are free text
   * and there's no library or distinct-values query, so it's the tags of the
   * first page of contacts (the address book's Add filter has the same gap).
   */
  async tagSuggestions(): Promise<string[]> {
    const seen = new Map<string, string>();
    const add = (t: string) => {
      const tag = t.trim();
      if (tag && !seen.has(tag.toLowerCase())) seen.set(tag.toLowerCase(), tag);
    };
    if (!RDX_VISITORS_ENV.PLATFORM_MODE) {
      await demoDelay();
      Object.values(demoTagLibrary).flat().forEach(add);
      demoVisitors
        .filter((v) => v.isInAddressBook !== false)
        .forEach((v) => (v.tags ?? []).forEach(add));
    } else {
      const { visitors } = await this.getPage({ skip: 0, take: TAKE_CAP });
      visitors.forEach((v) => (v.tags ?? []).forEach(add));
    }
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  }

  /** Typeahead over the address book (Add-visit "suggested" list). */
  async suggest(term: string): Promise<ApiVisitor[]> {
    const { visitors } = await this.getPage({ skip: 0, take: 8, searchTerm: term });
    return visitors;
  }

  /** Which of these visitors have visits (delete-block). */
  async idsWithVisits(visitorIds: string[]): Promise<string[]> {
    if (!RDX_VISITORS_ENV.PLATFORM_MODE) {
      await demoDelay();
      return visitorIds.filter((id) => demoVisits.some((v) => v.visitorId === id));
    }
    // One page of the visits grid, filtered to these people: anybody with a
    // visit has at least one row. 100 ids at a time, the grid's page cap.
    const found = new Set<string>();
    for (let i = 0; i < visitorIds.length; i += 100) {
      const ids = visitorIds.slice(i, i + 100);
      const data = await this.gql.gql<{
        pagedVisitsGrid: { visits: { visitorId?: string | null }[] };
      }>(
        `query VisitorIdsWithVisits($filter: FilterGroupInput) {
          pagedVisitsGrid(skip: 0, take: 100, filter: $filter) { visits { visitorId } }
        }`,
        { filter: { op: 'AND', rules: [{ field: 'visitorId', operator: 'IN', values: ids }] } },
      );
      for (const visit of data.pagedVisitsGrid.visits) {
        if (visit.visitorId) found.add(visit.visitorId);
      }
    }
    return visitorIds.filter((id) => found.has(id));
  }

  async create(input: VisitorInput): Promise<ApiVisitor> {
    if (!RDX_VISITORS_ENV.PLATFORM_MODE) {
      await demoDelay();
      const created: ApiVisitor = {
        _id: demoId('p'),
        ...input,
        isInAddressBook: input.isInAddressBook ?? true,
        tags: input.tags ?? [],
        createdAt: new Date().toISOString(),
        visitorType: 'Standard guest',
      };
      demoVisitors.push(created);
      return created;
    }
    // A person created here is a saved contact: `createVisitor` puts them in
    // the address book itself. VIP is its own mutation on the platform.
    const data = await this.gql.gql<{ createVisitor: ApiVisitor }>(
      `mutation CreateVisitor($input: CreateVisitorInput!) {
        createVisitor(input: $input) { ${FIELDS} }
      }`,
      { input: toVisitorInput(input as unknown as Record<string, unknown>) },
    );
    const created = adaptVisitor(data.createVisitor);
    if (input.vip && !created.vip) {
      await this.setVip(created._id, true);
      return { ...created, vip: true };
    }
    return created;
  }

  /**
   * Bulk create (CSV import) — one `createVisitor` per row, a few at a time.
   * The platform has no batch create; `importVisitors` takes its own CSV
   * format, and this import maps the app's template itself.
   */
  async createMany(inputs: VisitorInput[]): Promise<number> {
    if (!RDX_VISITORS_ENV.PLATFORM_MODE) {
      for (const input of inputs) await this.create(input);
      return inputs.length;
    }
    let created = 0;
    for (let i = 0; i < inputs.length; i += IMPORT_CONCURRENCY) {
      const batch = inputs.slice(i, i + IMPORT_CONCURRENCY);
      await Promise.all(batch.map((input) => this.create(input)));
      created += batch.length;
    }
    return created;
  }

  /** Full update — the input requires firstName/lastName every time. */
  async update(id: string, input: VisitorInput): Promise<void> {
    if (!RDX_VISITORS_ENV.PLATFORM_MODE) {
      const v = demoVisitors.find((x) => x._id === id);
      if (v) {
        Object.assign(v, input);
        // The server resolves the type label from the id; mirror that here.
        if (input.visitorTypeId) {
          v.visitorType =
            DEMO_VISITOR_TYPES.find((t) => t._id === input.visitorTypeId)?.name ?? v.visitorType;
        }
      }
      // Keep visit snapshots plausible in demo mode.
      for (const visit of demoVisits)
        if (visit.visitorId === id && visit.visitor) Object.assign(visit.visitor, input);
      return demoDelay();
    }
    await this.gql.gql(
      `mutation UpdateVisitor($id: ID!, $input: CreateVisitorInput!) {
        updateVisitor(id: $id, input: $input) { _id }
      }`,
      { id, input: toVisitorInput(input as unknown as Record<string, unknown>) },
    );
  }

  async delete(id: string): Promise<void> {
    if (!RDX_VISITORS_ENV.PLATFORM_MODE) {
      const i = demoVisitors.findIndex((v) => v._id === id);
      if (i >= 0) demoVisitors.splice(i, 1);
      return demoDelay();
    }
    await this.gql.gql(`mutation DeleteVisitor($id: ID!) { deleteVisitor(id: $id) }`, { id });
  }

  async bulkDelete(ids: string[]): Promise<void> {
    if (!RDX_VISITORS_ENV.PLATFORM_MODE) {
      for (const id of ids) {
        const i = demoVisitors.findIndex((v) => v._id === id);
        if (i >= 0) demoVisitors.splice(i, 1);
      }
      return demoDelay();
    }
    await this.gql.gql(
      `mutation BulkDeleteVisitors($ids: [ID!]!) { bulkDeleteVisitors(ids: $ids) }`,
      { ids },
    );
  }

  /**
   * A PIN is unique per site, so the platform needs the site it is for: the
   * rail's selected location. Refused by the API without one.
   */
  async generatePin(id: string, locationId?: string | null): Promise<string | null> {
    if (!RDX_VISITORS_ENV.PLATFORM_MODE) {
      const v = demoVisitors.find((x) => x._id === id);
      const pin = String(Math.floor(1000 + Math.random() * 9000));
      if (v) v.pin = pin;
      await demoDelay();
      return pin;
    }
    const data = await this.gql.gql<{ generatePinForVisitor: { pin?: string | null } | null }>(
      `mutation GeneratePinForVisitor($id: ID!, $locationId: ID) {
        generatePinForVisitor(id: $id, locationId: $locationId) { pin }
      }`,
      { id, locationId: locationId ?? null },
    );
    return data.generatePinForVisitor?.pin ?? null;
  }

  async setVip(id: string, vip: boolean): Promise<void> {
    if (!RDX_VISITORS_ENV.PLATFORM_MODE) {
      const v = demoVisitors.find((x) => x._id === id);
      if (v) v.vip = vip;
      return demoDelay();
    }
    await this.gql.gql(
      `mutation UpdateVisitorVIPStatus($id: ID!, $vip: Boolean!) { updateVisitorVIPStatus(id: $id, vip: $vip) }`,
      { id, vip },
    );
  }
}
